import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { 
  fetchNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification,
  deleteSelectedNotifications,
  deleteAllNotifications,
  requestPushPermission
} from "../services/notificationService";
import { useNotificationSound } from "./useNotificationSound";

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isUserActive, setIsUserActive] = useState(true);
  const { playSound } = useNotificationSound();
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  // Detectar actividad del usuario para polling adaptativo
  useEffect(() => {
    let inactivityTimer: NodeJS.Timeout;

    const handleActivity = () => {
      setIsUserActive(true);
      clearTimeout(inactivityTimer);
      
      // Marcar como inactivo después de 2 minutos sin actividad
      inactivityTimer = setTimeout(() => {
        setIsUserActive(false);
      }, 120000);
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Iniciar timer
    handleActivity();

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      clearTimeout(inactivityTimer);
    };
  }, []);

  // Polling adaptativo: 10s cuando activo, 30s cuando inactivo
  const pollingInterval = isUserActive ? 10000 : 30000;

  const { data: notifications, isLoading, error } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => user ? fetchNotifications(user.id) : Promise.resolve([]),
    enabled: !!user,
    refetchInterval: pollingInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 5000,
  });

  // Real-time subscription para notificaciones instantáneas
  useEffect(() => {
    if (!user) return;

    console.log('🔔 Suscribiéndose a notificaciones en tiempo real para:', user.id);

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 Nueva notificación recibida:', payload.new);
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 Notificación actualizada:', payload.new);
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        }
      )
      .subscribe((status) => {
        console.log('🔔 Estado de suscripción:', status);
      });

    return () => {
      console.log('🔔 Desuscribiéndose de notificaciones');
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const markAsRead = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const markAllAsRead = useMutation({
    mutationFn: () => user ? markAllNotificationsAsRead(user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const enablePushNotifications = async () => {
    if (!user) return { success: false, message: "User not logged in" };
    
    try {
      const success = await requestPushPermission();
      return {
        success,
        message: success
          ? "Push notifications enabled successfully"
          : "Failed to enable push notifications"
      };
    } catch (error) {
      console.error("Error enabling push notifications:", error);
      return {
        success: false,
        message: "Error enabling push notifications"
      };
    }
  };

  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => user ? deleteNotification(notificationId, user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const deleteSelectedMutation = useMutation({
    mutationFn: (notificationIds: string[]) => user ? deleteSelectedNotifications(notificationIds, user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => user ? deleteAllNotifications(user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const unreadCount = notifications?.filter(n => !n.read).length || 0;

  // Reproducir sonido cuando hay nuevas notificaciones no leídas
  useEffect(() => {
    if (unreadCount > previousUnreadCount && previousUnreadCount > 0) {
      playSound();
    }
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    deleteSelected: deleteSelectedMutation.mutate,
    deleteAll: deleteAllMutation.mutate,
    isPending: markAsRead.isPending || markAllAsRead.isPending || 
               deleteNotificationMutation.isPending || deleteSelectedMutation.isPending || deleteAllMutation.isPending,
    enablePushNotifications
  };
};
